/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useContractAttachmentSearchMutation, useContractDeleteByIdMutation, useContractGetByIdMutation} from '../Contract.api'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { IContractDto, IContractFilter, IContractStatus } from '../Contract.model'
import classNames from 'classnames'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IContractAttachmentDto } from '../ContractAttachment.model'
import { FilePicker } from '../../../common/component/FilePicker'
import { IAttachment } from '../../media/Media.model'
import { DeleteConfirmationModal } from '../../../common/component/modal/DeleteConfirmationModal'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { parseLocalDate } from '../../../util/Locale'
import { IAssetFilter } from '../../asset/Asset.model'
import { useAppSelector } from '../../store/Store'
import { selectAssets, selectAssetsPagination } from '../../asset/Asset.slice'
import { useAssetLoadMoreMutation, useAssetSearchMutation } from '../../asset/Asset.api'
import { ListContent } from '../../../common/component/ListContent'
import { AssetInnerListItem } from '../../asset/components/AssetInnerListItem'
import { renderIdentifier } from '../../../util/Constants'
import { ContactEditor } from '../../contact/components/ContactEditor'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'

const ContractDetailsPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {contractId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CONTRACTS'}),
      path: '/contracts',
      isSeparator: false,
      isActive: false,
    },
  ]
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/contracts/${contractId}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.ASSET'}),
      icon: 'bi-cpu',
      action: () => navigate(`/assets/new?contractId=${contractId}`),
    },
  ]
  const removeSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.DELETE'}),
    targetModal: '#contract_delete_confirmation_modal'
  }
  
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [openDropDown, setOpenDropDown] = useState(false)

  const didRequestDelete = useRef(false)
  const didFetchContract = useRef(false)
  const [isContractLoading, setIsContractLoading] = useState(true)
  const [contract, setContract] = useState<IContractDto>()
  const [contractGetById] = useContractGetByIdMutation()
  const [contractDeleteById] = useContractDeleteByIdMutation()

  const didFetchAttachments = useRef(false)
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false)
  const [attachments, setAttachments] = useState<IContractAttachmentDto[]>()
  const [contractAttachmentSearch] = useContractAttachmentSearchMutation()

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchContract = async (contractId) => {
      try {
        if (!didFetchContract.current) {
          setIsContractLoading(true)
          const response: any = await contractGetById({id: contractId})
          if (response && response.data) {
            const contract: IContractDto = response.data
            setContract(contract)
            searchAttachment(contract.id)
          }
        }
      // } catch (e: any) {
      //   setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
      } finally {
        setIsContractLoading(false)
      }
    }

    const searchAttachment = async (contractId) => {
      try {
        if (!didFetchAttachments.current) {
          setIsAttachmentsLoading(true)
          const response: any = await contractAttachmentSearch({
            filters: [{id: IContractFilter.HASH, value: contractId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            setAttachments(response.data.data)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAttachmentsLoading(false)
      }
    }

    if (contractId) {
      fetchContract(contractId)
      fetchAssetData(contractId)
    }
  }, [])

  const deleteConfirmOnClickHandler = async () => {
    try {
      if (contractId && !didRequestDelete.current) {
        setIsContractLoading(true)
        const response: any = await contractDeleteById(contractId)
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          navigate(`/contracts`)
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsContractLoading(false)
    }
  }

  //--------------------------------
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)

  const [assetSearch] = useAssetSearchMutation()
  const [assetLoadMore] = useAssetLoadMoreMutation()

  const assets = useAppSelector(selectAssets)
  const assetsPagination = useAppSelector(selectAssetsPagination)

  const [isSearchLoading, setIsSearchLoading] = useState(false)

  const fetchAssetData = async (contractId: string) => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await assetSearch({
          filters: [{id: IAssetFilter.CONTRACT_HASH, value: contractId, editable: false}],
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current && contractId) {
        await assetLoadMore({
          pagination: {
            limit: assetsPagination?.limit,
            sort: assetsPagination?.sort,
            page: assetsPagination?.page,
          },
          filters: [{id: IAssetFilter.CONTRACT_HASH, value: contractId, editable: false}],
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {contract && (
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fs-2 fw-bolder'>
                        {`${contract?.client?.displayName} - ${contract?.documentType}`}
                      </span>
                      <span className='fw-bolder text-primary me-1 fs-5'>
                        {renderIdentifier(contract.sequence)}
                      </span>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-row-auto flex-start ms-5'>
                  {contract && (
                    <>
                      <button
                        className='btn btn-sm btn-light-primary me-2'
                        onClick={() => setOpenDropDown((prev) => !prev)}
                        disabled={isContractLoading}
                      >
                        <i className='bi bi-three-dots fs-5'></i>
                      </button>
                      <DropdownMenu
                        visible={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        settingOptions={settingOptions}
                        deleteOption={removeSettingOption}
                      ></DropdownMenu>
                    </>
                  )}

                  <button className='btn btn-sm btn-light' onClick={() => navigate(`/contracts`)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isContractLoading ? (
            <Spinner />
          ) : !contract ? (
            <div className='row'>
              <div className='d-flex flex-row-fluid flex-start mt-10'>
                <span className='text-danger '>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    <span className='fs-6 me-1'>
                      <i className='text-danger me-1 bi bi-exclamation-octagon' />
                      {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                      {intl.formatMessage({id: 'UTIL.SELLERS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                      {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    </a>
                  </li>
                  {assets && assets.length > 0 && (
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_5'>
                        {intl.formatMessage({id: 'UTIL.ASSETS'})}
                      </a>
                    </li>
                  )}
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <ClientEmbedered client={contract.client} />
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                            value={parseLocalDate(
                              contract.createdAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                            value={parseLocalDate(
                              contract.updatedAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ID'})}
                            value={`${contract.sequence}`}
                          ></CustomInputRenderer>

                          <div className='row mb-5'>
                            <label className='col-xxl-3 text-gray-700 fs-6'>
                              {intl.formatMessage({id: 'UTIL.STATUS'})}
                            </label>
                            <div
                              className={classNames({
                                'col-xxl-9 ps-6': contract.status === IContractStatus.ACTIVE,
                                'col-xxl-9 ps-6 text-danger fw-bold':
                                  contract.status !== IContractStatus.ACTIVE,
                              })}
                            >
                              {intl.formatMessage({id: `UTIL.STATUS.${contract.status}`})}
                            </div>
                          </div>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DATE_START'})}
                            value={parseLocalDate(contract.startDate, '', '', {
                              hours: false,
                              minutes: false,
                              seconds: false,
                              millis: false,
                            })}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DATE_END'})}
                            value={parseLocalDate(contract.endDate, '', '', {
                              hours: false,
                              minutes: false,
                              seconds: false,
                              millis: false,
                            })}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DOCUMENT_TYPE'})}
                            value={contract.documentType}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_TYPE'})}
                            value={contract.serviceType}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                            value={contract.serviceFrequency}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                            value={contract.serviceLocation}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.RESPONSE_TIME'})}
                            value={contract.serviceResponseTime}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_AGREEMENT'})}
                            value={contract.serviceAgreement}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COMMENTS'})}
                            value={contract.comments}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.METRICS'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                            value={`${contract.totalAttachment || 0}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSETS'})}
                            value={`${contract.totalAsset || 0}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                            value={`${contract.totalReport || 0}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      {contract.contacts && (
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor data={contract.contacts} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                    <div className='row mb-5'>
                      {contract.sellers && (
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor data={contract.sellers} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                    <div className='row mb-5'>
                      <FilePicker
                        loading={isAttachmentsLoading}
                        uploadedFiles={attachments?.map((attachment) => {
                          const file: IAttachment = {
                            id: attachment.id,
                            fileName: attachment.fileName,
                            temporalPath: attachment.temporalPath,
                            path: attachment.path,
                            size: attachment.size,
                            contentType: attachment.contentType,
                            comments: attachment.comments,
                            status: attachment.status,
                            processing: false,
                            uploaded: true,
                          }
                          return file
                        })}
                      />
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_5' role='tabpanel'>
                    <div className='row mb-5'>
                      <ListContent
                        loading={isSearchLoading}
                        nextPage={assetsPagination?.page}
                        nextPageOnClickHandler={nextPageOnClickHandler}
                        listComponent={assets.map((item, index) => (
                          <div key={index}>
                            <AssetInnerListItem data={item}></AssetInnerListItem>
                            <div className='separator'></div>
                          </div>
                        ))}
                      ></ListContent>
                    </div>
                  </div>
                </div>
              </div>

              <DeleteConfirmationModal
                id='contract_delete_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                  .replace(/%s/g, intl.formatMessage({id: 'UTIL.CONTRACT'}))}
                description={`${contract?.client?.displayName} - ${contract?.documentType} - #${contract?.sequence}`}
                confirmOnClickHandler={deleteConfirmOnClickHandler}
              ></DeleteConfirmationModal>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export {ContractDetailsPage}
